import { useAuth, useUser } from '@clerk/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { RoutesList } from '../config/routes-list';
import { useLanguageHydration } from '../hooks/auth/use-language-hydration';
import { useMemberships } from '../hooks/auth/use-memberships';
import { useProvisioningGrace } from '../hooks/auth/use-provisioning-grace';
import { useValidatedTenantId } from '../hooks/auth/use-validated-tenant-id';
import { useConsent } from '../hooks/consent/use-consent';
import { useSubscription } from '../hooks/subscription/use-subscription';
import { useOnboardingStatus } from '../hooks/use-onboarding';
import { usePermissions } from '../hooks/use-permissions';
import { useSignupLinkClaim } from '../hooks/use-signup-link-claim';
import { tenantContextAtom, updateTenantContextAtom } from '../stores/auth.store';
import { BootstrapError } from './bootstrap-error';
import { ConsultantProfileInactiveScreen } from './consultant-profile-inactive-screen';
import { FullScreenSpinner } from './full-screen-spinner';
import {
  decideTenantSelection,
  getPostOnboardingRedirect,
  isBootstrappingAuth,
  resolveMembershipsEmptyState,
  shouldShowConsultantBlockedScreen,
  shouldTrackProvisioningGrace,
} from './protected-route.helpers';

function useTenantDecision() {
  const tenantContext = useAtomValue(tenantContextAtom);
  const updateTenantContext = useSetAtom(updateTenantContextAtom);
  const {
    memberships,
    isPlatformAdmin,
    consultantAccessBlocked,
    isPending: membershipsPending,
    isError: membershipsError,
  } = useMemberships();
  const autoSelectedTenantIdRef = useRef<string | null>(null);

  const decision = decideTenantSelection({
    hasTenantContext: !!tenantContext?.currentTenantId,
    currentTenantId: tenantContext?.currentTenantId ?? null,
    memberships: memberships.map((m) => ({ tenantId: m.tenantId })),
    isPlatformAdmin,
  });

  useEffect(() => {
    if (decision.kind === 'auto-select' && autoSelectedTenantIdRef.current !== decision.tenantId) {
      autoSelectedTenantIdRef.current = decision.tenantId;
      const membership = memberships.find((m) => m.tenantId === decision.tenantId);
      if (membership) {
        updateTenantContext({
          tenantId: membership.tenantId,
          tenantName: membership.tenantName,
        });
      }
    }
  }, [decision, memberships, updateTenantContext]);

  return {
    decision,
    memberships,
    isPlatformAdmin,
    consultantAccessBlocked,
    membershipsPending,
    membershipsError,
  };
}

export default function ProtectedRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const location = useLocation();
  const {
    data: onboardingStatus,
    isPending: onboardingPending,
    isError: onboardingError,
  } = useOnboardingStatus();
  const {
    needsTosAcceptance,
    needsConsentPrompt,
    isConsentsPending,
    isError: consentError,
  } = useConsent();
  const {
    decision,
    memberships,
    isPlatformAdmin,
    consultantAccessBlocked,
    membershipsPending,
    membershipsError,
  } = useTenantDecision();
  const provisioningGraceExpired = useProvisioningGrace(
    shouldTrackProvisioningGrace(membershipsPending, memberships.length),
  );
  const { isPending: permissionsPending, isError: permissionsError } = usePermissions();
  const hasValidatedTenant = useValidatedTenantId() !== null;
  useSubscription();
  useSignupLinkClaim();
  useLanguageHydration();

  if (
    isBootstrappingAuth({
      isLoaded,
      isSignedIn: !!isSignedIn,
      membershipsPending,
      tenantDecisionKind: decision.kind,
      hasValidatedTenant,
      onboardingPending,
      consentPending: isConsentsPending,
      permissionsPending,
    })
  ) {
    return <FullScreenSpinner />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (
    shouldShowConsultantBlockedScreen({
      membershipsPending,
      membershipsCount: memberships.length,
      consultantAccessBlocked,
    })
  ) {
    return <ConsultantProfileInactiveScreen />;
  }

  if (membershipsError || onboardingError || permissionsError || consentError) {
    return <BootstrapError />;
  }

  const membershipsEmptyState = resolveMembershipsEmptyState({
    membershipsCount: memberships.length,
    provisioningGraceExpired,
  });
  if (membershipsEmptyState === 'spinner') {
    return <FullScreenSpinner />;
  }
  if (membershipsEmptyState === 'error') {
    return <BootstrapError />;
  }

  if (decision.kind === 'pick') {
    return <Navigate to={RoutesList.SELECT_COMPANY} replace />;
  }

  if (!onboardingStatus) {
    return <FullScreenSpinner />;
  }

  const redirectPath = getPostOnboardingRedirect({
    onboardingCompleted: onboardingStatus.onboardingCompleted,
    passwordEnabled: user?.passwordEnabled ?? true,
    hasSsoAccount: (user?.externalAccounts?.length ?? 0) > 0,
    trialExpired: onboardingStatus.trialExpired,
    needsTosOrConsent: needsTosAcceptance || needsConsentPrompt,
    isPlatformAdmin,
  });

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
}
